import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  Stack,
  FormControl,
  Select,
  Tooltip,
  IconButton,
  Chip,
  MenuItem,
  InputLabel,
  Link,
  Radio,
  Checkbox,
  Popover,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { hostApiClient } from '../../hostApiClient';
import { Room, UpdateRoomRequest } from '../../models/Room';
import MediaUploader from '../../components/MediaUploader';
import { RoomGear } from '../../models/RoomGear';
import { RoomAmenity } from '../../models/RoomAmenity';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import { getCategoryLabel, categoryIcons } from '../../categories';
import AmountInput from '../../components/AmountInput';
import { NotificationSnackbar, SnackbarSeverity } from '../../components/common/NotificationSnackbar';
import { GoogleCalendarOption } from '../../models/GoogleCalendar';
import { ORGANIZATION_GOOGLE_CALENDAR_HREF, GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED } from '../../utils/organizationSections';
import { ROOM_CALENDAR_COLOR_OPTIONS } from '../../utils/calendarColors';

const currencySymbols: Record<string, string> = {
  USD: '$',
  EUR: '€',
  PLN: 'zł',
  GBP: '£',
  AUD: 'A$',
};

const RoomDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [room, setRoom] = useState<Room | null>(null);
  const [editValues, setEditValues] = useState<UpdateRoomRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [gears, setGears] = useState<RoomGear[]>([]);
  const [newGear, setNewGear] = useState<RoomGear>({ gearType: '', details: '', pricePerH: 0 });
  const [amenities, setAmenities] = useState<RoomAmenity[]>([]);
  const [newAmenity, setNewAmenity] = useState<RoomAmenity>({ amenityType: '', details: '', pricePerH: 0 });
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>('success');
  const [googleCalendarOptions, setGoogleCalendarOptions] = useState<GoogleCalendarOption[]>([]);
  const [googleCalendarOptionsLoading, setGoogleCalendarOptionsLoading] = useState(false);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const data = await hostApiClient.getRoom(id!);
        const editData: UpdateRoomRequest = {
          name: data.room.name,
          description: data.room.description,
          imageUrls: data.room.imageUrls,
          amenities: data.amenities,
          gears: data.gears,
          pricePerH: data.room.pricePerH,
          area: data.room.area,
          areaUnit: data.room.areaUnit,
          googleCalendarInfo: data.room.googleCalendarInfo,
          categories: data.room.categories || [],
          primaryCategory: data.room.primaryCategory,
          color: data.room.color || '#1E88E5'
        };
        setCurrencyCode(data.currencyCode?.toString() || '');
        setRoom(data.room);
        setEditValues(editData);
        setGears(data.gears);
        setAmenities(data.amenities);
        
        if (GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED) {
          try {
            setGoogleCalendarOptionsLoading(true);
            const options = await hostApiClient.getGoogleCalendarOptions();
            setGoogleCalendarOptions(options);
          } catch (err) {
            console.error('Failed to fetch Google Calendar options:', err);
          } finally {
            setGoogleCalendarOptionsLoading(false);
          }
        } else {
          setGoogleCalendarOptions([]);
        }
      } catch (err) {
        console.error('Failed to fetch room', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [id]);

  const handleChange = (field: keyof UpdateRoomRequest, value: any) => {
    if (!editValues) return;
    setEditValues({ ...editValues, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!editValues || !id) return;
    try {
      await hostApiClient.updateRoom(id, editValues);
      setRoom(prev => ({ ...prev!, ...editValues }));
      setHasChanges(false);
      setSnackbarMessage(t('common.changesSaved', 'Changes saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to update room:', err);
      setSnackbarMessage(t('common.saveError', 'Failed to save changes'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    setEditValues(room ? {
      name: room.name,
      description: room.description,
      imageUrls: room.imageUrls,
      amenities: room.amenities,
      gears: room.gears,
      pricePerH: room.pricePerH,
      area: room.area,
      areaUnit: room.areaUnit,
      googleCalendarInfo: room.googleCalendarInfo,
      categories: room.categories || [],
      primaryCategory: room.primaryCategory,
      color: room.color || '#1E88E5'
    } : null);
    setHasChanges(false);
    setSnackbarMessage(t('common.changesCancelled', 'Changes cancelled'));
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAddGear = () => {
    if (!newGear.gearType.trim()) return;
    const updated = [...gears, { ...newGear, details: newGear.details?.trim() || '' }];
    setGears(updated);
    setEditValues(prev => prev ? { ...prev, gears: updated } : prev);
    setNewGear({ gearType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleDeleteGear = (index: number) => {
    const updated = gears.filter((_, i) => i !== index);
    setGears(updated);
    setEditValues(prev => prev ? { ...prev, gears: updated } : prev);
    setHasChanges(true);
  };

  const handleAddAmenity = () => {
    if (!newAmenity.amenityType.trim()) return;
    const updated = [...amenities, { ...newAmenity, details: newAmenity.details?.trim() || '' }];
    setAmenities(updated);
    setEditValues(prev => prev ? { ...prev, amenities: updated } : prev);
    setNewAmenity({ amenityType: '', details: '', pricePerH: 0 });
    setHasChanges(true);
  };

  const handleDeleteAmenity = (index: number) => {
    const updated = amenities.filter((_, i) => i !== index);
    setAmenities(updated);
    setEditValues(prev => prev ? { ...prev, amenities: updated } : prev);
    setHasChanges(true);
  };

  const handleToggleCategory = (category: string) => {
    if (!editValues) return;
    const currentCategories = editValues.categories || [];
    let updatedCategories: string[];
    
    if (currentCategories.includes(category)) {
      updatedCategories = currentCategories.filter((c) => c !== category);
      // If removing the primary category, clear it
      if (editValues.primaryCategory === category) {
        setEditValues((prev) => prev ? {
          ...prev,
          categories: updatedCategories,
          primaryCategory: updatedCategories.length > 0 ? updatedCategories[0] : undefined
        } : prev);
      } else {
        setEditValues((prev) => prev ? { ...prev, categories: updatedCategories } : prev);
      }
    } else {
      updatedCategories = [...currentCategories, category];
      setEditValues((prev) => prev ? {
        ...prev,
        categories: updatedCategories,
        primaryCategory: prev.primaryCategory || category // Auto-select as primary if none selected
      } : prev);
    }
    setHasChanges(true);
  };

  const handleSetPrimaryCategory = (category: string) => {
    setEditValues((prev) => prev ? { ...prev, primaryCategory: category } : prev);
    setHasChanges(true);
  };

  const handleRemoveCategory = (category: string, event: React.MouseEvent) => {
    event.stopPropagation();
    handleToggleCategory(category);
  };

  const handleSelectAllCategories = () => {
    if (!editValues) return;
    const allCategories = Object.keys(categoryIcons);
    const currentCategories = editValues.categories || [];
    if (currentCategories.length === allCategories.length) {
      // Deselect all
      setEditValues((prev) => prev ? {
        ...prev,
        categories: [],
        primaryCategory: undefined
      } : prev);
    } else {
      // Select all
      setEditValues((prev) => prev ? {
        ...prev,
        categories: allCategories,
        primaryCategory: prev.primaryCategory && allCategories.includes(prev.primaryCategory)
          ? prev.primaryCategory
          : allCategories[0]
      } : prev);
    }
    setHasChanges(true);
  };

  if (loading || !editValues) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1000, width: '100%', mx: 'auto' }}>
        <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h4" component="h1" gutterBottom>{editValues.name}</Typography>
          <Divider sx={{ mb: { xs: 2, md: 3 } }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label={t('roomDetails.roomName')} value={editValues.name} onChange={(e) => handleChange('name', e.target.value)} fullWidth />
              </Grid>
              
              {/* Categories Section */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1 }}>
                    {t('roomDetails.categories', 'Room Categories')}
                  </InputLabel>
                  <Box
                    onClick={(e) => setCategoryAnchorEl(e.currentTarget)}
                    sx={{
                      mt: 2,
                      minHeight: 56,
                      border: '1px solid',
                      borderColor: categoryAnchorEl ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(79, 95, 235, 0.04)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                      {editValues.categories && editValues.categories.length > 0 ? (
                        <>
                          {[...editValues.categories]
                            .sort((a, b) => {
                              // Primary category first
                              if (a === editValues.primaryCategory) return -1;
                              if (b === editValues.primaryCategory) return 1;
                              return 0;
                            })
                            .slice(0, 3)
                            .map((category) => {
                              const isPrimary = editValues.primaryCategory === category;
                              const color = isPrimary ? '#4F5FEB' : '#58a7b9';
                              return (
                                <Chip
                                  key={category}
                                  label={getCategoryLabel(category)}
                                  size="medium"
                                  onDelete={(e) => handleRemoveCategory(category, e)}
                                  deleteIcon={<CloseIcon sx={{ fontSize: '1rem' }} />}
                                  sx={{
                                    height: 32,
                                    bgcolor: color,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '16px',
                                    fontWeight: isPrimary ? 700 : 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    fontSize: '0.75rem',
                                    '& .MuiChip-label': {
                                      fontSize: '0.75rem',
                                      px: 1.5,
                                      fontWeight: 'inherit',
                                    },
                                    '& .MuiChip-deleteIcon': {
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      fontSize: '1rem',
                                      '&:hover': {
                                        color: 'white',
                                      },
                                    },
                                  }}
                                />
                              );
                            })}
                          {editValues.categories.length > 3 && (
                            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              +{editValues.categories.length - 3}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('roomDetails.selectCategories', 'Select categories...')}
                        </Typography>
                      )}
                    </Box>
                    {categoryAnchorEl ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </Box>
                  <FormHelperText>
                    {t('roomDetails.categoriesDescription', 'Select one or more categories that describe this room')}
                  </FormHelperText>
                </FormControl>

                <Popover
                  open={Boolean(categoryAnchorEl)}
                  anchorEl={categoryAnchorEl}
                  onClose={() => setCategoryAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: categoryAnchorEl?.offsetWidth || 300,
                        mt: 0.5,
                        maxHeight: 400,
                        borderRadius: 2,
                      },
                    },
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    {/* Select All */}
                    <ListItemButton
                      onClick={handleSelectAllCategories}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          checked={editValues.categories?.length === Object.keys(categoryIcons).length}
                          indeterminate={
                            (editValues.categories?.length || 0) > 0 &&
                            (editValues.categories?.length || 0) < Object.keys(categoryIcons).length
                          }
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                              color: '#4F5FEB',
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('common.selectAll', 'Select all')}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>

                    <Divider sx={{ my: 1 }} />

                    {/* Individual Categories */}
                    {Object.keys(categoryIcons).map((category) => {
                      const isSelected = editValues.categories?.includes(category);
                      const isPrimary = editValues.primaryCategory === category;

                      return (
                        <ListItemButton
                          key={category}
                          onClick={() => handleToggleCategory(category)}
                          sx={{
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                color: 'text.secondary',
                                '&.Mui-checked': {
                                  color: '#4F5FEB',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={getCategoryLabel(category)}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                            }}
                          />
                          {isSelected && (
                            <Radio
                              checked={isPrimary}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetPrimaryCategory(category);
                              }}
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                '&.Mui-checked': {
                                  color: '#4F5FEB',
                                },
                              }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Box>
                  
                  {editValues.categories && editValues.categories.length > 0 && (
                    <Box sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('roomDetails.primaryCategoryDescription', 'Click the radio button to select a primary category - it will be featured in search results')}
                      </Typography>
                    </Box>
                  )}
                </Popover>
              </Grid>

              <Grid item xs={12}>
                <TextField label={t('roomDetails.description')} value={editValues.description || ''} onChange={(e) => handleChange('description', e.target.value)} fullWidth multiline rows={10} />
              </Grid>
              <Grid item xs={12}>
              <Box display="flex" gap={2} alignItems="center">
                <AmountInput
                  amount={editValues.area?.toString() || ''}
                  label={t('roomDetails.roomArea')}
                  onAmountChange={(newAmount) => handleChange('area', parseFloat(newAmount) || 0)}
                  onErrorChange={() => {}}
                  currencySymbol={editValues.areaUnit}
                  width={200}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>{t('roomDetails.unit')}</InputLabel>
                  <Select
                    value={editValues.areaUnit}
                    label={t('roomDetails.unit')}
                    onChange={(e) => handleChange('areaUnit', e.target.value)}
                  >
                    <MenuItem value="m2">m²</MenuItem>
                    <MenuItem value="ft2">ft²</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
              <Grid item xs={12}>
                <MediaUploader label={t('roomDetails.roomImages')} initialMedia={editValues.imageUrls || []} onMediaChange={(media) => handleChange('imageUrls', media)} />
              </Grid>
            </Grid>
            {/* Gears Section */}
            <Stack spacing={2} mt={4} mb={4} marginRight={4}>
              <Typography variant="subtitle1">{t('roomDetails.gears')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <Select
                    value={newGear.gearType}
                    onChange={(e) => setNewGear({ ...newGear, gearType: e.target.value })}
                  >
                    {Object.keys(gearIcons).map((type) => (
                      <MenuItem key={type} value={type}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {gearIcons[type]}
                          <span>{getGearLabel(type)}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('roomDetails.details')}
                  size="small"
                  value={newGear.details}
                  onChange={(e) => setNewGear({ ...newGear, details: e.target.value })}
                />
                <AmountInput
                  amount={newGear.pricePerH.toString()}
                  label={t('roomDetails.pricePerHour')}
                  onAmountChange={(newAmount) => setNewGear({ ...newGear, pricePerH: parseFloat(newAmount) || 0 })}
                  onErrorChange={() => {}}
                  currencySymbol={currencySymbols[currencyCode] || '$'}
                  width={120}
                />
                <Tooltip title={t('roomDetails.addGear')}>
                  <IconButton color="primary" onClick={handleAddGear}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center' }}>
                  {gears.map((gear, index) => (
                    <Chip
                      key={index}
                      label={`${getGearLabel(gear.gearType)}${gear.details ? ': ' + gear.details : ''}${gear.pricePerH ? ` - ${gear.pricePerH.toFixed(2)} ${currencyCode}` : ''}`}
                      variant="outlined"
                      size="small"
                      onDelete={() => handleDeleteGear(index)}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>

            {/* Amenities Section */}
            <Stack spacing={2} mt={4} mb={4} marginRight={4}>
              <Typography variant="subtitle1">{t('roomDetails.amenities')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <Select
                    value={newAmenity.amenityType}
                    onChange={(e) => setNewAmenity({ ...newAmenity, amenityType: e.target.value })}
                  >
                    {Object.keys(amenityIcons).map((type) => (
                      <MenuItem key={type} value={type}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {amenityIcons[type]}
                          <span>{getAmenityLabel(type)}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('roomDetails.details')}
                  size="small"
                  value={newAmenity.details}
                  onChange={(e) => setNewAmenity({ ...newAmenity, details: e.target.value })}
                />
                <AmountInput
                  amount={newAmenity.pricePerH.toString()}
                  label={t('roomDetails.pricePerHour')}
                  onAmountChange={(newAmount) => setNewAmenity({ ...newAmenity, pricePerH: parseFloat(newAmount) || 0 })}
                  onErrorChange={() => {}}
                  currencySymbol={currencySymbols[currencyCode] || '$'}
                  width={120}
                />
                <Tooltip title={t('roomDetails.addAmenity')} placement="left">
                  <IconButton color="primary" onClick={handleAddAmenity}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center' }}>
                  {amenities.map((amenity, index) => (
                    <Chip
                      key={index}
                      label={`${getAmenityLabel(amenity.amenityType)}${amenity.details ? ': ' + amenity.details : ''}${amenity.pricePerH ? ` - ${amenity.pricePerH.toFixed(2)} ${currencyCode}` : ''}`}
                      variant="outlined"
                      size="small"
                      onDelete={() => handleDeleteAmenity(index)}
                    />
                  ))}
                </Box>
              </Box>
            </Stack>

            {/* Google Calendar Integration */}
            {GOOGLE_CALENDAR_INLINE_PICKERS_ENABLED ? (
              <Stack spacing={2} mt={4} mb={4} marginRight={4}>
                <Typography variant="subtitle1">
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarTodayIcon fontSize="small" />
                    {t('roomDetails.googleCalendarIntegration', 'Google Calendar Integration')}
                  </Box>
                </Typography>
                
                {googleCalendarOptionsLoading ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      {t('roomDetails.loadingGoogleCalendars', 'Loading Google Calendars...')}
                    </Typography>
                  </Box>
                ) : googleCalendarOptions.length === 0 ? (
                  <Box
                    sx={{
                      backgroundColor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 3,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('roomDetails.googleCalendarNotEnabled', 'Google Calendar integration is not enabled for your organization.')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('roomDetails.enableGoogleCalendar', 'To sync room bookings with Google Calendar, enable it under Integrations → Google Calendar.')}
                    </Typography>
                    <Link
                      href={ORGANIZATION_GOOGLE_CALENDAR_HREF}
                      variant="body2"
                      color="primary"
                      sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                    >
                      {t('roomDetails.goToOrganizationSettings', 'Go to Organization Settings')} →
                    </Link>
                  </Box>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('roomDetails.selectGoogleCalendar', 'Select Google Calendar')}</InputLabel>
                    <Select
                      value={editValues.googleCalendarInfo?.id || ''}
                      label={t('roomDetails.selectGoogleCalendar', 'Select Google Calendar')}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedOption = googleCalendarOptions.find(opt => opt.id === selectedId);
                        handleChange('googleCalendarInfo', selectedOption || null);
                      }}
                    >
                      <MenuItem value="">
                        <em>{t('roomDetails.noCalendarSelected', 'No calendar selected')}</em>
                      </MenuItem>
                      {googleCalendarOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            ) : null}

              {/* Room Color Selection */}
              <Stack spacing={2} mt={4} mb={4} marginRight={4}>
                <Typography variant="subtitle1">
                  {t('roomDetails.color', 'Room Color')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {ROOM_CALENDAR_COLOR_OPTIONS.map((colorOption) => {
                    const isSelected = editValues.color === colorOption.color;
                    return (
                      <Box
                        key={colorOption.color}
                        onClick={() => handleChange('color', colorOption.color)}
                        sx={{
                          width: 48,
                          height: 48,
                          backgroundColor: colorOption.color,
                          borderRadius: 1,
                          cursor: 'pointer',
                          border: isSelected ? '4px solid' : '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          position: 'relative',
                          boxShadow: isSelected 
                            ? '0 0 0 3px rgba(25, 118, 210, 0.2), 0 4px 8px rgba(0, 0, 0, 0.2)' 
                            : 'none',
                          transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                          '&:hover': {
                            transform: 'scale(1.15)',
                            boxShadow: isSelected 
                              ? '0 0 0 3px rgba(25, 118, 210, 0.3), 0 6px 12px rgba(0, 0, 0, 0.3)' 
                              : '0 2px 8px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                        title={t(`roomDetails.colorNames.${colorOption.key}`, t(`room.colorNames.${colorOption.key}`, colorOption.key))}
                      >
                        {isSelected && (
                          <CheckIcon 
                            sx={{ 
                              color: 'white', 
                              fontSize: 28,
                              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
                              stroke: 'black',
                              strokeWidth: 0.5,
                            }} 
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t('roomDetails.colorDescription', 'This color will be used to display room events in the calendar')}
                </Typography>
              </Stack>

            <Grid item xs={12}>
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f0f8ff',
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 3,
                  p: 3,
                  mt: 2,
                })}
              >
                <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                  💰 {t('roomDetails.setRoomPricing')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    mb: 2,
                    color: theme.palette.text.secondary,
                  })}
                >
                  {t('roomDetails.basePriceInfo')}
                </Typography>
                <AmountInput
                  amount={editValues?.pricePerH.toString() || '0'}
                  label={t('roomDetails.pricePerHour')}
                  onAmountChange={(newAmount) => handleChange('pricePerH', parseFloat(newAmount) || 0)}
                  onErrorChange={() => {}}
                  currencySymbol={currencySymbols[currencyCode] || '$'}
                  width={120}
                />
              </Box>
            </Grid>

          <Box display="flex" gap={2} mt={4} justifyContent="flex-end">
            <Button variant="contained" onClick={handleSave} disabled={!hasChanges}>{t('common.save')}</Button>
            <Button variant="outlined" onClick={handleCancel} disabled={!hasChanges}>{t('common.cancel')}</Button>
          </Box>
        </Card>
        
        <NotificationSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleSnackbarClose}
        />
      </Box>
    </>
  );
};

export default RoomDetailsPage;
